import React, { useState } from 'react';

import FilePicker from './ApideckFilePickerCustom';
import { File } from './ApideckFilePickerCustom/types';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { useApideckUploadFilesV2 } from '@/api/mutations/useApideckUploadFilesV2';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { ApideckFileData } from '@/types';

interface CustomFilePickerProps {
  token: string;
  matterId: string;
  closeModalFunction: React.Dispatch<React.SetStateAction<boolean>>;
  initialConnection: string;
  onApideckSuccess?: () => void;
}

export const CustomFilePicker = ({
  token,
  matterId,
  closeModalFunction,
  initialConnection,
  onApideckSuccess,
}: CustomFilePickerProps) => {
  const [selectedSource, setSelectedSource] = useState<string>('');
  const [selectedFiles, setSelectedFiles] = useState<Record<string, ApideckFileData>>({});

  // FILE / FOLDER SELECTION HANDLING
  const addOrUpdateFile = (id: string, file: File, connectedSource: string) => {
    setSelectedFiles((prevState) => ({
      ...prevState,
      [id]: {
        id: file.id,
        name: file.name,
        source: selectedSource || connectedSource,
        size: file.size ?? 0,
        type: file.type !== 'folder' ? 'file' : 'folder',
      },
    }));
  };

  const removeFile = (id: string) => {
    setSelectedFiles((prevState) => {
      const newState = { ...prevState };
      delete newState[id];
      return newState;
    });
  };

  const handleSelect = (file: File, selectedSource: string) => {
    if (selectedFiles[file.id]) {
      removeFile(file.id);
    } else {
      addOrUpdateFile(file.id, file, selectedSource);
    }
  };

  const resetSelection = () => {
    setSelectedFiles({});
  };

  // MODAL CLOSE HANDLING
  const closeModal = () => {
    resetSelection();
    setSelectedSource('');
    closeModalFunction(false);
  };

  // UPLOAD HANDLING
  const [isUploading, setIsUploading] = useState(false);
  const [isError, setIsError] = useState(false);
  const onErrorUpload = () => {
    trackCustomEvent(TrackingEventsV2.IntegrationUploadUnsuccessful);
    setIsError(true);
  };
  const onSuccessUpload = async () => {
    if (onApideckSuccess) {
      // Allow some time to prevent a race condition and for the animation.
      await new Promise((resolve) => setTimeout(resolve, 1000));
      onApideckSuccess();
    }
    trackCustomEvent(TrackingEventsV2.IntegrationUploadSuccessful);
    closeModal();
  };
  const { mutateAsync } = useApideckUploadFilesV2({ onError: onErrorUpload, onSuccess: onSuccessUpload });
  const handleUpload = async () => {
    setIsUploading(true);
    trackCustomEvent(TrackingEventsV2.IntegrationUploadAttempted, { items: Object.keys(selectedFiles).length });
    await mutateAsync({ matterId, fileArray: Object.values(selectedFiles) });
  };

  return isUploading ? (
    isError ? (
      <div className="flex h-[44rem] flex-col items-center justify-center rounded-lg bg-gray-100 p-12 shadow-lg">
        <div className="relative mb-4 h-24 w-24">
          <Icon className="lucide--times absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform text-6xl text-red-500" />
        </div>
        <p className="text-center text-lg font-semibold text-gray-700">Something went wrong.</p>
        <p className="text-center text-lg font-semibold text-gray-700">Please try again.</p>
      </div>
    ) : (
      <div className="flex h-[44rem] flex-col items-center justify-center rounded-lg bg-gray-100 p-12 shadow-lg">
        <div className="relative mb-4 h-24 w-24">
          <Icon className="lucide--cloud absolute top-0 left-1/2 -translate-x-1/2 -translate-y-2 transform text-6xl text-blue-500" />
          <Icon className="lucide--arrow-up absolute bottom-0 left-1/3 -translate-x-1/16 transform animate-bounce text-4xl text-blue-500" />
        </div>
        <p className="text-center text-lg font-semibold text-gray-700">The upload will be done in the background.</p>
      </div>
    )
  ) : (
    <div className="h-[44rem] p-14">
      <FilePicker
        onSelect={handleSelect}
        token={token}
        selectedObject={selectedFiles}
        resetSelection={resetSelection}
        onConnectionSelect={(connection) => {
          setSelectedSource(connection.service_id);
          trackCustomEvent(TrackingEventsV2.IntegrationSelected, { integration: connection.service_id });
          resetSelection();
        }}
        initialConnection={initialConnection}
      />
      <hr className="my-6 border-gray-200" />
      <div className="flex items-center justify-end space-x-4 px-4 py-5 sm:px-6">
        <Button
          variant="primary"
          onClick={resetSelection}
          data-tooltip-id="agreed-tooltip"
          data-tooltip-target="tooltip-hover"
          data-tooltip-trigger="hover"
          data-tooltip-content="Clear selection"
        >
          <Icon className="lucide--eraser mr-2 text-white" />
          Clear
        </Button>
        <Button
          variant="primary"
          onClick={async () => await handleUpload()}
          data-tooltip-id="agreed-tooltip"
          data-tooltip-target="tooltip-hover"
          data-tooltip-trigger="hover"
          data-tooltip-content="Upload to Wexler"
        >
          <Icon className="lucide--folder-plus mr-2 text-white" />
          Upload
        </Button>
      </div>
    </div>
  );
};
